"use client";

import { useEffect, useRef, useState } from "react";

import { OctogenSpinner } from "@octogen/schema-analyzer/loading";

import { useNavigationMenu } from "@/hooks/useNavigationMenu";
import type { BrowseMenuRootSummary } from "@/lib/browse-menu-roots";

import { DESKTOP_NAVBAR_MEGA_MENU_TOP_PX } from "../layout-constants";
import { TaxonomyMegaMenu } from "../megamenu/TaxonomyMegaMenu";

import { TaxonomyLink } from "./TaxonomyLink";

interface TaxonomyNavigationProps {
  activeCategory?: string | null;
  onCategoryClick?: (category: string) => void;
  onMegaMenuChange?: (isOpen: boolean) => void;
  browseMenuRoots?: BrowseMenuRootSummary[] | null;
  className?: string;
  style?: React.CSSProperties;
}

export function TaxonomyNavigation({
  activeCategory,
  onCategoryClick,
  onMegaMenuChange,
  browseMenuRoots = null,
  className = "",
  style,
}: TaxonomyNavigationProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const menu = useNavigationMenu(browseMenuRoots);
  const navRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const root = menu.index?.roots.find((item) => item.uuid === selected);
  const load = () => {
    void menu.ensureLoaded().catch(() => {});
  };

  useEffect(() => {
    onMegaMenuChange?.(selected !== null);
  }, [selected, onMegaMenuChange]);
  useEffect(() => {
    if (selected === null) return;
    const outside = (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setSelected(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelected(null);
        // A removed root's trigger may no longer exist in the current snapshot.
        if (triggerRef.current?.isConnected) triggerRef.current.focus();
        else navRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", key);
    };
  }, [selected]);

  return (
    <div ref={navRef} className={className} style={style}>
      <nav className="flex items-center gap-[28px] py-[9px]" data-testid="desktop-taxonomy-nav">
        {menu.roots.map((category) => (
          <TaxonomyLink
            key={category.uuid}
            label={category.display_name}
            active={
              selected ? selected === category.uuid : activeCategory === category.display_name
            }
            expanded={selected === category.uuid}
            controls={`desktop-root-${category.uuid}`}
            onClick={(event) => {
              triggerRef.current = event.currentTarget;
              if (selected === category.uuid) {
                setSelected(null);
                return;
              }
              setSelected(category.uuid);
              onCategoryClick?.(category.display_name);
              load();
            }}
          />
        ))}
        {menu.rootsLoading && <OctogenSpinner label="Loading categories" />}
        {menu.rootsError && <button onClick={menu.retryRoots}>Retry categories</button>}
      </nav>
      {selected !== null && (
        <div id={`desktop-root-${selected}`}>
          {root ? (
            <TaxonomyMegaMenu
              key={root.uuid}
              isOpen
              rootNode={root}
              departments={root.children_nodes ?? []}
              onClose={() => setSelected(null)}
            />
          ) : (
            <div
              className="fixed left-0 right-0 z-50 bg-white p-6 shadow-md"
              style={{ top: DESKTOP_NAVBAR_MEGA_MENU_TOP_PX }}
              aria-live="polite"
            >
              {menu.status === "error" ? (
                <>
                  <p>Categories could not be loaded.</p>
                  <button onClick={load}>Retry</button>
                </>
              ) : menu.status === "ready" ? (
                <p>This category is no longer available. Choose an updated category above.</p>
              ) : (
                <OctogenSpinner label="Loading category" />
              )}
              <button
                className="ml-4"
                onClick={() => {
                  setSelected(null);
                  triggerRef.current?.focus();
                }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
